import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import WorkOrderForm from '@/components/manufacturing/WorkOrderForm';
import ManufacturingService from '@/api/manufacturing.service';
import { canCreate } from '@/lib/permissions';

const CreateWorkOrder: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const businessId = parseInt(localStorage.getItem('selectedBusiness') ? JSON.parse(localStorage.getItem('selectedBusiness')!).id : '1');

  // Check permissions
  if (!canCreate('Manufacturing')) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/manufacturing/work-orders')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Work Orders
          </Button>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <ClipboardList className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Permission Denied</h3>
              <p className="text-muted-foreground">
                You don't have permission to create work orders.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (data: any) => {
    try {
      setIsLoading(true);

      const workOrderData = {
        businessId,
        workOrderNumber: data.workOrderNumber,
        productionJobId: data.productionJobId,
        operation: data.operation,
        description: data.description || '',
        assignedTo: data.assignedTo || '',
        estimatedHours: data.estimatedHours,
        scheduledStartDate: data.scheduledStartDate.toISOString(),
        scheduledEndDate: data.scheduledEndDate.toISOString(),
        notes: data.notes || '',
        status: 'pending'
      };

      const response = await ManufacturingService.createWorkOrder(workOrderData);

      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Work order created successfully",
        });
        navigate('/manufacturing/work-orders');
      } else {
        throw new Error(response.data.message || 'Failed to create work order');
      }
    } catch (error: any) {
      console.error('Error creating work order:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create work order",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/manufacturing/work-orders');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/manufacturing/work-orders')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Work Orders
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Create Work Order</h1>
          <p className="text-muted-foreground">
            Create a new work order for manufacturing operations
          </p>
        </div>
      </div>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Work Order Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <WorkOrderForm
            mode="create"
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={isLoading}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateWorkOrder; 